import Footer from './components/Footer';
import LandingNav from './components/LandingNav';
import { footerNavigation } from './contentSections';

interface Goal {
  title: string;
  detail: string;
}

interface Phase {
  number: number;
  title: string;
  status: 'active' | 'upcoming' | 'future';
  tagline: string;
  description: string;
  why: string;
  goals: Goal[];
  milestones: string[];
}

const phases: Phase[] = [
  {
    number: 1,
    title: 'Foundation — Build & Listen',
    status: 'active',
    tagline: 'Establish the platform, gather the community, and refine based on real feedback.',
    description:
      'The first phase focuses on building a solid, trustworthy home for Canadian Members online. We are launching core directory and community features, onboarding founding organizations, and actively listening to users to shape the direction of the platform.',
    why:
      'Before adding complexity, the platform must earn trust. A scattered community cannot be served by a half-built tool. Phase 1 is about getting the fundamentals right — making sure Centers can list themselves accurately, community members can find what they need, and the people using App feel heard and respected from day one.',
    goals: [
      {
        title: 'Launch core platform features',
        detail:
          'The initial release includes an organization directory (Centers, Community schools, charities, Member businesses), community notice boards, event listings with RSVP support, user profiles, and a lost-and-found board. Each feature is designed to solve a real, immediate need for Canadian Member communities.',
      },
      {
        title: 'Onboard founding organizations across major cities',
        detail:
          'We are prioritizing outreach to Centers, Community centres, and organizations in Toronto, Montreal, Vancouver, Calgary, Edmonton, Ottawa, and Winnipeg — cities home to the largest communities in Canada. Each organization gets a dedicated profile page they can claim and manage.',
      },
      {
        title: 'Establish a structured community feedback loop',
        detail:
          'Feedback is not an afterthought — it is built into the platform from the start. This includes in-platform suggestion forms, monthly community calls open to all members, a public changelog showing what has changed and why, and a transparent roadmap that is updated based on what the community asks for.',
      },
      {
        title: 'Build bilingual readiness (English & French)',
        detail:
          'Canada is a bilingual country and a significant portion of Canadian Members live in Quebec and francophone communities across the country. Phase 1 ensures the platform architecture supports full bilingual content from the outset, so French-language communities are not an afterthought in Phase 3 or 4.',
      },
      {
        title: 'Establish data integrity and community safety standards',
        detail:
          'All organization listings are verified before going live. A community reporting system allows members to flag inaccurate or inappropriate content. A moderation framework is published openly so the community understands how decisions are made. The platform will never sell user data or use manipulative engagement patterns.',
      },
      {
        title: 'Launch the App mobile-responsive web experience',
        detail:
          'Most Canadian Members access information on their phones. Before a dedicated native app is built in Phase 3, the web platform is designed to work seamlessly on mobile browsers — fast loading, easy navigation, and touch-friendly interfaces that feel native even on a browser.',
      },
    ],
    milestones: [
      'Platform publicly launched and accessible at App.ca',
      '50+ organizations listed across 5 provinces',
      'Community notice board active with verified organizations posting',
      'First public feedback session held with community members',
      'Public changelog and roadmap page published',
    ],
  },
  {
    number: 2,
    title: 'Community Depth — Resources for Every Generation',
    status: 'upcoming',
    tagline: 'Tailor the platform to serve every age group and role within the Member community.',
    description:
      'Community emphasizes the rights and needs of every member of the community — from the youngest child to the oldest elder. Phase 2 builds dedicated resource hubs and experiences for each group, ensuring no one is left behind.',
    why:
      'A generic community platform serves no one particularly well. The needs of a Member senior in Halifax are fundamentally different from those of a Member teenager in Mississauga or a new mother in Saskatoon. Phase 2 is about recognizing that the Member community is not monolithic — it is made up of distinct groups with distinct needs — and building for each of them intentionally.',
    goals: [
      {
        title: 'Launch a Seniors Hub',
        detail:
          'Member seniors are among the most underserved members of the community online. This hub will surface local elder-care services run by or aligned with Community values, community prayer and transportation assistance programs, community visitation and companionship initiatives, and Native or Community educational programs adapted for older adults. It will be designed with larger text, simpler navigation, and high contrast for ease of use.',
      },
      {
        title: 'Build a Children & Youth section',
        detail:
          'Parents need a trusted place to find Community education for their children — Philosophy classes, Native lessons, Community school directories, youth camps, and after-school programs. This section also surfaces age-appropriate Community content, connects youth with Member Student Associations at universities, and highlights Local recreational activities and youth leadership programs.',
      },
      {
        title: 'Develop a Families & Parenting portal',
        detail:
          'Member families need resources that address the unique challenges of raising children with Community values in a Canadian context. This includes directories of Community schools and weekend centers, family-friendly event listings, resources on navigating Canadian family law from an Community perspective, and access to Member family counsellors and social workers.',
      },
      {
        title: 'Launch a Fathers Circle',
        detail:
          'Member fathers in Canada often lack spaces to discuss the responsibilities and challenges of fatherhood through an community lens. This section connects fathers with mentorship programs, community groups focused on fatherhood in community, resources on financial responsibilities, and local study circles oriented toward men and family leadership.',
      },
      {
        title: 'Launch a Sisters Circle',
        detail:
          'A dedicated, moderated space for Member women to share resources, find events, and connect with other women in their community. This includes directories of women-only fitness facilities, community classes for women, maternal support networks, women\'s study groups, and resources for Member women navigating the workplace or academic institutions.',
      },
      {
        title: 'Create a New Members hub',
        detail:
          'Converting to Community in Canada can be an isolating experience. This hub provides step-by-step guidance for new Members: registering with a local Center, learning how to participate, connecting with a community mentor (buddy system), accessing introductory community learning resources in plain language, and navigating questions about lifestyle changes with compassionate, non-judgmental support.',
      },
      {
        title: 'Partner with Community scholars and educators for content curation',
        detail:
          'All resources surfaced through these hubs will be reviewed and validated in partnership with qualified community scholars and educators. This ensures that content is sound, contextually appropriate for a Canadian Member audience, and reflective of the diversity of traditions present in Canada.',
      },
    ],
    milestones: [
      'Seniors Hub live with 20+ verified elder-care and support resources',
      'Children & Youth section launched with Community school directory for all provinces',
      'Sisters Circle active with moderation guidelines published',
      'New Members hub launched with buddy system matching feature',
      'Partnership with at least 3 Canadian community scholars for content review',
    ],
  },
  {
    number: 3,
    title: 'Accessibility & Reach — Technology for All',
    status: 'future',
    tagline: 'Make App reachable for every Member in Canada, regardless of ability or device.',
    description:
      'A platform that truly serves the community must be accessible to everyone — including those with disabilities, those in remote areas, and those who rely on mobile devices as their primary means of access.',
    why:
      'Accessibility is not a feature — it is a value. A platform that excludes people with disabilities, non-English speakers, or those without reliable internet access is not truly serving the community. Community\'s emphasis on inclusion demands that we build technology that reaches the margins, not just the mainstream.',
    goals: [
      {
        title: 'Achieve WCAG 2.1 AA accessibility compliance',
        detail:
          'This includes full screen reader compatibility (tested with NVDA, JAWS, and VoiceOver), logical keyboard navigation throughout the platform, sufficient colour contrast ratios on all text and interactive elements, focus indicators on all interactive components, alt text on all images, and captions on all video content. An accessibility audit by a third-party accessibility specialist will be commissioned before this milestone is marked complete.',
      },
      {
        title: 'Launch native mobile apps for iOS and Android',
        detail:
          'A dedicated App mobile app brings the platform into everyday Member life — schedule notifications, event reminders, one-tap access to community notices, and the ability to post and update listings on the go. The app will be built with React Native to share logic with the web platform. Push notifications will be opt-in and respect user privacy.',
      },
      {
        title: 'Add multilingual support for key community languages',
        detail:
          'Canadian Members speak many languages. Phase 3 introduces full interface and content translations for Native, Urdu, Somali, Dari/Pashto, and French — languages widely spoken among Canadian Members beyond English.',
      },
      {
        title: 'Build offline-capable features for low-connectivity users',
        detail:
          'Not all Canadian Members have reliable high-speed internet — particularly those in rural areas, northern communities, or lower-income households. Key features such as saved organization profiles, community schedules, and community notices will be available offline after first load, using Progressive Web App (PWA) technology.',
      },
      {
        title: 'Develop comprehensive community resource guides',
        detail:
          'Beyond community content, Canadian Members often need help navigating systems: finding Local food banks, accessing mental health services from Member-sensitive providers, understanding tenant rights, finding employment after immigration, or accessing legal aid. Phase 3 builds curated, province-by-province guides for housing, employment, mental health, legal aid, and financial literacy — all filtered for Member-friendly providers where available.',
      },
      {
        title: 'Partner with community social service organizations',
        detail:
          'Organizations like ISNA Canada, Member Welfare Centre, Khalil Centre, and Community Social Services Association (ISSA) have decades of experience serving the community. Phase 3 formalizes partnerships to co-publish directories and resource guides, ensuring the information is accurate, up-to-date, and vetted by organizations with lived experience in the community.',
      },
    ],
    milestones: [
      'Third-party WCAG 2.1 AA accessibility audit completed and remediation shipped',
      'iOS and Android apps live in the App Store and Google Play',
      'Full Native and French translations live',
      'Community resource guides published for Ontario, BC, Alberta, and Quebec',
      'PWA offline mode active for key features',
    ],
  },
  {
    number: 4,
    title: 'Newcomers & Cultural Inclusion — A Home for All Members in Canada',
    status: 'future',
    tagline: 'Support refugees, immigrants, and culturally diverse Member communities in finding their footing.',
    description:
      'Canada welcomes thousands of Member newcomers every year. Many arrive without knowing where the nearest Center is, how to navigate Canadian systems, or how to connect with others from their home country. Phase 4 builds the infrastructure to welcome and support them.',
    why:
      'The Member community in Canada is one of the most ethnically diverse in the world — bringing together communities from Somalia, Pakistan, Egypt, Syria, Afghanistan, Bangladesh, Iran, Nigeria, and dozens of other countries. This richness is a gift, but it also means that "the Member community" is not one community — it is many. Phase 4 ensures that every community, regardless of origin, finds recognition, connection, and support on App.',
    goals: [
      {
        title: 'Launch a Newcomers & Settlement Hub',
        detail:
          'A step-by-step guide for Member immigrants and refugees arriving in Canada. This hub covers: registering with the nearest Center, finding Local resources in your city, enrolling children in Community schools, accessing government settlement services, understanding the Canadian healthcare system, opening a bank account, finding employment resources, and connecting with a community sponsor. Content will be available in multiple languages and written in plain, non-legalistic language.',
      },
      {
        title: 'Partner with established Canadian Member settlement agencies',
        detail:
          'Organizations like ISNA Canada, Member Welfare Centre (Toronto), Community Family & Social Services Association (Edmonton), and local Community Community Services have been serving newcomers for decades. App will not reinvent the wheel — instead, it will serve as a discovery and navigation layer on top of existing services, directing newcomers to the right organizations with the right contacts.',
      },
      {
        title: 'Build country-of-origin diaspora community groups',
        detail:
          'Many newcomers want to connect with others who share their cultural background, language, and community tradition. App will host diaspora community hubs for major groups including Somali, Pakistani, Egyptian, Moroccan, Syrian, Bangladeshi, Turkish, and West African Member communities — each with their own cultural centres directory, language-specific resources, and community events calendar.',
      },
      {
        title: 'Create cultural centre directories by ethnic and linguistic background',
        detail:
          'Finding a Center that matches your style or a cultural centre that speaks your language can make the difference between thriving and feeling invisible. App\'s cultural centre directory will tag organizations by cultural background, languages spoken, and accessibility features — so newcomers can find a home that feels familiar.',
      },
      {
        title: 'Develop a dedicated Refugee Support section',
        detail:
          'Member refugees face distinct challenges beyond general immigration: trauma, family separation, legal uncertainty, and the urgency of immediate needs. This section provides fast access to emergency housing contacts, refugee legal clinics, Local resources, community sponsors willing to help, mental health services experienced with refugee trauma, and guides to the Canadian refugee determination process.',
      },
      {
        title: 'Launch the Hijra Guide — relocating between Canadian cities',
        detail:
          'Many Members move between Canadian cities for work, school, or family reasons and need to quickly rebuild their community ties in a new city. The Community Guide is a practical relocation resource: find the nearest Center, identify Community schools for children, connect with local Member professional networks, and discover community events in your new city — all within minutes of arrival.',
      },
      {
        title: 'Coordinate with organizations serving specific newcomer populations',
        detail:
          'Different newcomer groups have different needs and existing support structures. App will establish formal coordination with organizations focused on Somali Canadian communities, Afghan refugees, Syrian resettlement programs, and Black Member communities — ensuring each group\'s unique history and needs are reflected in the platform, not flattened into a generic "newcomer" category.',
      },
    ],
    milestones: [
      'Newcomers Hub live with city-specific settlement guides for 7 major cities',
      'Formal partnerships with 5+ settlement agencies',
      'Diaspora community hubs launched for 8+ ethnic communities',
      'Refugee Support section live with emergency contact directories',
      'Hijra Guide available for all major Canadian cities',
    ],
  },
  {
    number: 5,
    title: 'Sustainability — A Self-Funding Community Platform',
    status: 'future',
    tagline: 'Build a platform that serves the community indefinitely — without depending on external funding.',
    description:
      'Long-term impact requires long-term sustainability. Phase 5 introduces ethical, community-aligned revenue models that allow App to grow and operate independently, staying true to its mission while never placing financial burdens on the communities it serves.',
    why:
      'Too many community-serving platforms collapse because they rely on grants, donors, or venture capital that eventually dries up or comes with strings attached. App is committed to a different path: a sustainable model rooted in Community financial principles, built on voluntary contribution and community ownership rather than extraction. The goal is for App to become a legacy — a continuing benefit — that outlasts any individual contributor or funding cycle.',
    goals: [
      {
        title: 'Launch a voluntary community support model',
        detail:
          'Community members and organizations who find value in App can contribute voluntarily through a simple "Support App" mechanism — a one-time or recurring contribution at any amount they choose. This is not a paywall or a subscription — it is an invitation to those who can give to support those who cannot. All contributions will be disclosed in the annual impact report.',
      },
      {
        title: 'Introduce an optional premium tier for organizations',
        detail:
          'Core features will always be free for all organizations. A premium tier will offer advanced tools for organizations that need them: detailed analytics on profile engagement, priority placement in search results, bulk content management tools, branded event pages, and API access for integration with their own websites. Pricing will be set below market rate and tiered by organization size.',
      },
      {
        title: 'Build an ethical Local Business Advertising Network',
        detail:
          'Member-owned businesses currently have no effective way to reach the Canadian Member community at scale. App will build a community-first advertising network that allows Local-certified businesses to be featured to relevant audiences — without invasive tracking, without algorithmic manipulation, and without content that conflicts with Community values. Advertisers are vetted for Local compliance before being accepted.',
      },
      {
        title: 'Establish a Waqf (Community endowment) model',
        detail:
          'An endowment is a form of permanent charitable asset that generates ongoing benefit. App will invite community investors and donors to contribute to a platform endowment fund, the returns of which fund ongoing operations. This model transforms App from a platform dependent on recurring revenue into a community-owned asset that generates its own sustainability. Legal structure and Community finance compliance will be established in partnership with qualified scholars.',
      },
      {
        title: 'Develop co-funding partnerships with Canadian Member charities',
        detail:
          'Several large Canadian Member charities have significant operational budgets and digital presence needs. App will offer co-funding partnerships in which charities contribute to platform operations in exchange for enhanced visibility, premium tools, joint campaigns, and data insights — creating a model where the platform and charities grow together rather than competing for the same donor pool.',
      },
      {
        title: 'Explore community cooperative ownership structure',
        detail:
          'As the platform matures, App will explore transitioning to a community cooperative or trust structure — where member organizations have formal governance rights, vote on platform direction, and share in the value the platform creates. This ensures App can never be sold to outside interests, acquired by a corporate entity, or have its mission redirected away from serving the community.',
      },
      {
        title: 'Publish an annual Community Impact Report',
        detail:
          'Transparency is a core value. Every year, App will publish a full accounting of: total users and organizations on the platform, communities represented by province, revenue sources and expenditures broken down in detail, the volunteer and paid team who make it run, what the community asked for and what was shipped, and what the plans are for the coming year. This report will be available publicly and sent to all registered organizations.',
      },
    ],
    milestones: [
      '"Support App" voluntary contribution model live',
      'Premium organizational tier launched with at least 10 paid organizations',
      'First Local business advertising partners onboarded',
      'Waqf legal structure established with Community finance scholar review',
      'First Annual Community Impact Report published and distributed',
      'Governance consultation with community organizations on cooperative model initiated',
    ],
  },
];

const statusConfig = {
  active: {
    label: 'In Progress',
    badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    dotClass: 'bg-green-500',
    borderClass: 'border-green-500',
    headerClass: 'bg-green-50/50 dark:bg-green-950/20',
  },
  upcoming: {
    label: 'Upcoming',
    badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    dotClass: 'bg-blue-500',
    borderClass: 'border-blue-500',
    headerClass: 'bg-blue-50/50 dark:bg-blue-950/20',
  },
  future: {
    label: 'Planned',
    badgeClass: 'bg-muted text-muted-foreground',
    dotClass: 'bg-muted-foreground',
    borderClass: 'border-border',
    headerClass: 'bg-muted/20',
  },
};

export default function RoadmapPage() {
  return (
    <div className='bg-background text-foreground'>
      <LandingNav />
      <main className='mx-auto max-w-4xl px-6 py-32 lg:px-8'>
        {/* Header */}
        <div className='mb-16'>
          <h1 className='text-4xl font-bold mb-4'>Platform Roadmap</h1>
          <p className='text-muted-foreground text-lg leading-relaxed max-w-2xl mb-4'>
            App is being built in deliberate phases — each one deepening our service to the{' '}
            <span className='font-medium text-foreground'>Member community across Canada</span>.
            Here is where we are going, and how we plan to get there.
          </p>
          <p className='text-muted-foreground text-sm leading-relaxed max-w-2xl'>
            This roadmap is a living document. It is updated based on community feedback, platform
            usage, and the evolving needs of Canadian Members. Every phase is shaped by the people
            it is meant to serve.
          </p>
        </div>

        {/* Legend */}
        <div className='flex flex-wrap gap-4 mb-12'>
          {(Object.entries(statusConfig) as [keyof typeof statusConfig, typeof statusConfig['active']][]).map(([, cfg]) => (
            <span key={cfg.label} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${cfg.badgeClass}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${cfg.dotClass}`} />
              {cfg.label}
            </span>
          ))}
        </div>

        {/* Timeline */}
        <div className='relative'>
          {/* Vertical line */}
          <div className='absolute left-6 top-0 bottom-0 w-px bg-border hidden sm:block' />

          <div className='space-y-16'>
            {phases.map((phase) => {
              const config = statusConfig[phase.status];
              return (
                <div key={phase.number} className='relative sm:pl-20'>
                  {/* Phase dot */}
                  <div
                    className={`absolute left-0 top-1 hidden sm:flex h-12 w-12 items-center justify-center rounded-full border-2 bg-background ${config.borderClass}`}
                  >
                    <span className='text-sm font-bold text-foreground'>{phase.number}</span>
                  </div>

                  {/* Card */}
                  <div className={`rounded-xl border-2 ${config.borderClass} bg-card overflow-hidden`}>
                    {/* Card header band */}
                    <div className={`px-6 pt-6 pb-4 ${config.headerClass}`}>
                      <div className='flex flex-wrap items-start justify-between gap-3 mb-2'>
                        <div>
                          <div className='flex items-center gap-2 mb-1 sm:hidden'>
                            <span className='text-xs font-bold text-muted-foreground uppercase tracking-wide'>
                              Phase {phase.number}
                            </span>
                          </div>
                          <h2 className='text-xl font-semibold'>{phase.title}</h2>
                        </div>
                        <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${config.badgeClass}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${config.dotClass}`} />
                          {config.label}
                        </span>
                      </div>
                      <p className='text-sm font-medium text-foreground/70 italic'>{phase.tagline}</p>
                    </div>

                    <div className='px-6 pb-6 pt-4 space-y-6'>
                      {/* Overview */}
                      <div>
                        <p className='text-muted-foreground text-sm leading-relaxed'>{phase.description}</p>
                      </div>

                      {/* Why this phase */}
                      <div className='rounded-lg bg-muted/40 border border-border/60 px-4 py-3'>
                        <p className='text-xs font-semibold text-foreground/50 uppercase tracking-wide mb-1'>Why this matters</p>
                        <p className='text-sm text-muted-foreground leading-relaxed'>{phase.why}</p>
                      </div>

                      {/* Goals */}
                      <div>
                        <h3 className='text-sm font-semibold text-foreground mb-3'>Goals &amp; Initiatives</h3>
                        <div className='space-y-3'>
                          {phase.goals.map((goal, i) => (
                            <div key={i} className='rounded-lg border border-border/60 bg-background/50 px-4 py-3'>
                              <p className='text-sm font-medium text-foreground mb-1'>{goal.title}</p>
                              <p className='text-sm text-muted-foreground leading-relaxed'>{goal.detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Milestones */}
                      <div>
                        <h3 className='text-sm font-semibold text-foreground mb-3'>Key Milestones</h3>
                        <ul className='space-y-1.5'>
                          {phase.milestones.map((m, i) => (
                            <li key={i} className='flex items-start gap-2 text-sm text-muted-foreground'>
                              <span className='mt-0.5 shrink-0 text-foreground/30'>◇</span>
                              <span>{m}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className='mt-20 rounded-xl border border-border bg-muted/30 p-8 text-center'>
          <h2 className='text-2xl font-semibold mb-3'>Help Shape the Roadmap</h2>
          <p className='text-muted-foreground leading-relaxed mb-2 max-w-xl mx-auto'>
            This platform belongs to the community. If you have ideas, needs, or feedback — we want to hear
            from you. Every suggestion helps us serve Canadian Members better.
          </p>
          <p className='text-muted-foreground text-sm leading-relaxed mb-6 max-w-xl mx-auto'>
            You can reach out to discuss partnerships, propose resources for a specific hub, flag
            gaps in the roadmap, or simply share what your community needs most.
          </p>
          <a
            href='mailto:info@App.ca'
            className='inline-flex items-center rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity'
          >
            Share Your Feedback
          </a>
        </div>
      </main>
      <Footer footerNavigation={footerNavigation} />
    </div>
  );
}



